/**
 * SOUND-643 — build the pre-submit summary for MID application creation.
 *
 * `/companies/:id/create` posts an ARRAY of applications in one action, one per
 * selected processor. Today it submits straight from the button with no
 * confirmation, and two silent fallbacks decide where those applications land:
 *
 *   - a processorId that matches no known processor is submitted as
 *     "Unknown Processor"
 *   - a company with no legal name and no DBA is submitted as "Unknown Company"
 *
 * Neither is surfaced. Since these records are the trigger for outbound
 * processor submissions, "which processor is this actually going to" has to be
 * answerable BEFORE the click, not diagnosed afterwards from a row that says
 * Unknown.
 *
 * Pure and tested; the modal that renders it cannot be tested in this repo.
 */

export const UNKNOWN_PROCESSOR = "Unknown Processor";
export const UNKNOWN_COMPANY = "Unknown Company";

/**
 * @param {object} args
 * @param {string[]} args.processorIds    selected processorIds (may contain blanks)
 * @param {object[]} args.merchants      the processor registry [{ processorId, name }]
 * @param {object}   args.registration   the company's registration
 * @param {string}   args.selectedDbaId
 * @returns {{
 *   rows: Array<{ processorId, processorName, resolved }>,
 *   count: number,
 *   companyName: string,
 *   dbaName: string|null,
 *   isParentCorp: boolean,
 *   warnings: Array<{ code: string, message: string }>,
 *   blocking: boolean
 * }}
 */
export function buildSubmissionSummary({
  processorIds = [],
  merchants = [],
  registration = null,
  selectedDbaId = "",
} = {}) {
  const cleaned = processorIds.filter((id) => id && String(id).trim() !== "");

  const dbas = registration?.dbas || [];
  const selectedDba = dbas.find((d) => d.dbaId === selectedDbaId) || null;
  const dbaName = selectedDba?.dbaName || null;
  const legalName = registration?.businessInfo?.legalName || null;
  const companyName = dbaName || legalName || UNKNOWN_COMPANY;

  const rows = cleaned.map((id) => {
    const processor = merchants.find((m) => m.processorId === id);
    return {
      processorId: id,
      processorName: processor?.name || UNKNOWN_PROCESSOR,
      resolved: Boolean(processor?.name),
    };
  });

  const warnings = [];

  // The one that matters most: an application whose destination could not be
  // resolved would be created against a processor nobody can name.
  const unresolved = rows.filter((r) => !r.resolved);
  if (unresolved.length > 0) {
    warnings.push({
      code: "unresolved-processor",
      message:
        unresolved.length === 1
          ? `1 processor could not be identified and would be submitted as "${UNKNOWN_PROCESSOR}".`
          : `${unresolved.length} processors could not be identified and would be submitted as "${UNKNOWN_PROCESSOR}".`,
    });
  }

  if (companyName === UNKNOWN_COMPANY) {
    warnings.push({
      code: "unknown-company",
      message: `This company has no legal name and no DBA selected — applications would be submitted as "${UNKNOWN_COMPANY}".`,
    });
  }

  // Duplicates create two applications to the SAME processor for the same DBA,
  // which downstream reads as a double submission.
  const seen = new Set();
  const dupes = new Set();
  for (const r of rows) {
    if (seen.has(r.processorId)) dupes.add(r.processorName);
    seen.add(r.processorId);
  }
  if (dupes.size > 0) {
    warnings.push({
      code: "duplicate-processor",
      message: `The same processor is selected more than once: ${[...dupes].join(", ")}.`,
    });
  }

  return {
    rows,
    count: rows.length,
    companyName,
    dbaName,
    isParentCorp: !dbaName,
    warnings,
    // Nothing is hard-blocked — internal may have a reason. The gate is that
    // the operator has to SEE it and confirm, which is the whole point.
    blocking: false,
  };
}
